
import React, { useEffect, useRef } from 'react';

type Stat =
  | { isText: true; value: string; label: string; prefix?: never; counterTarget?: never }
  | { isText?: false; prefix: string; counterTarget: number; label: string; value?: never };

const stats: Stat[] = [
  { prefix: '+', counterTarget: 10, label: 'Anos de Experiência' },
  { prefix: '+', counterTarget: 1000, label: 'Cirurgias Realizadas' },
  { isText: true, value: 'UNESP', label: 'Formação em Cirurgia Plástica' },
  { isText: true, value: 'Pós-Emagrecimento', label: 'Especialização em Remodelamento Corporal' },
];

const StatsSection: React.FC = () => {
  const root = useRef<HTMLDListElement>(null);
  const scope = useRef<any>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(el);

        import('animejs').then(({ animate, stagger, createScope }) => {
          scope.current = createScope({ root: el }).add(() => {
            animate('.stat-item', {
              opacity: [0, 1],
              translateY: [20, 0],
              duration: 600,
              ease: 'outExpo',
              delay: stagger(150, { start: 100 }),
            });

            el.querySelectorAll<HTMLElement>('.stat-counter').forEach((counterEl) => {
              const target = Number(counterEl.dataset.target ?? 0);
              counterEl.textContent = target.toString();
              animate({ value: 0 }, {
                value: target,
                duration: 1400,
                ease: 'outExpo',
                delay: 200,
                onUpdate: (anim) => {
                  counterEl.textContent = Math.round((anim.targets[0] as { value: number }).value).toString();
                },
              });
            });
          });
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      scope.current?.revert();
    };
  }, []);

  const StatItem = ({ stat }: { stat: Stat }) => (
    <div className="stat-item flex flex-col items-center justify-center py-6 px-6 md:px-4 shrink-0 md:flex-1">
      {stat.isText ? (
        <>
          <dd className="text-base md:text-lg text-gold tracking-[0.08em] uppercase font-semibold text-center mb-1 m-0">
            {stat.value}
          </dd>
          <dt className="text-xs md:text-xs text-light-muted tracking-[0.2em] uppercase text-center">
            {stat.label}
          </dt>
        </>
      ) : (
        <>
          <dd className="flex items-start m-0">
            <span className="text-xl md:text-2xl font-light text-gold mt-1 mr-0.5">{stat.prefix}</span>
            {/* Valor final no HTML: é o que robô, leitor sem JS e o .md do
                prerender enxergam. A animação reinicia do zero ao entrar na tela. */}
            <span className="stat-counter text-3xl md:text-4xl font-light text-gold tracking-tight" data-target={stat.counterTarget}>
              {stat.counterTarget}
            </span>
          </dd>
          <dt className="text-xs md:text-xs text-light-muted tracking-[0.2em] uppercase text-center mt-1">
            {stat.label}
          </dt>
        </>
      )}
    </div>
  );

  return (
    <section aria-label="Indicadores e formação" className="py-10 md:py-14 bg-dark-base border-y border-white/5 overflow-hidden">
      <h2 className="sr-only">Números e formação</h2>
      {/* Desktop: grid normal */}
      <dl className="hidden md:block container mx-auto px-6 lg:px-12" ref={root}>
        <div className="grid grid-cols-4 divide-x divide-white/10">
          {stats.map((stat, index) => (
            <StatItem key={index} stat={stat} />
          ))}
        </div>
      </dl>

      {/* Mobile: marquee contínuo (valores estáticos — sem counter animation) */}
      <div className="md:hidden relative overflow-hidden" aria-hidden="true">
        <div
          className="flex w-max animate-[marquee_18s_linear_infinite]"
          style={{ willChange: 'transform' }}
        >
          {[...stats, ...stats].map((stat, index) => (
            <div key={index} className="stat-item flex flex-col items-center justify-center py-2 px-8 shrink-0 border-r border-white/10">
              {stat.isText ? (
                <>
                  <span className="text-sm text-gold tracking-[0.08em] uppercase font-semibold text-center mb-0.5 whitespace-nowrap">
                    {stat.value}
                  </span>
                  <span className="text-xs text-light-muted tracking-[0.15em] uppercase text-center whitespace-nowrap">
                    {stat.label}
                  </span>
                </>
              ) : (
                <>
                  <div className="flex items-start">
                    <span className="text-lg font-light text-gold mt-0.5 mr-0.5">{stat.prefix}</span>
                    <span className="text-2xl font-light text-gold tracking-tight">
                      {stat.counterTarget}
                    </span>
                  </div>
                  <span className="text-xs text-light-muted tracking-[0.15em] uppercase text-center whitespace-nowrap">
                    {stat.label}
                  </span>
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
